import { useEffect, useState } from 'react';
import {
  AssociatedDoctorProps,
  GetAssociatedDoctorItem,
  PaginatedDoctorItem,
  PickListCardProps,
} from '../interface/interface';
import { PickList } from './PickList';
import { getPaginatedDoctors } from '../service';
import {
  addAssociatedClinics,
  breakAssociations,
  getAssociatedClinics,
} from '../service/clinic';
import showToast from './ToastComponent';

function PickListCard(props: PickListCardProps) {
  return (
    <div className="relative flex items-start pb-4 pt-3.5 px-3">
      <div className="min-w-0 flex-1 text-sm leading-6">
        <label htmlFor={`${props.id}-dr`} className="font-medium text-gray-900">
          Dr.{props.name}
        </label>
        <p id="comments-description" className="text-gray-500">
          {props.email}
        </p>
      </div>
      <div className="ml-3 flex h-6 items-center">
        <input
          id={`${props.id}-dr`}
          aria-describedby="comments-description"
          name={`${props.id}-dr`}
          checked={
            props?.itemsOnTheStack?.find((x) => x.id === props.id)
              ? true
              : false
          }
          onChange={(e) => {
            if (e.target.checked) {
              props.onStack();
            } else {
              props.onRemove();
            }
          }}
          type="checkbox"
          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
        />
      </div>
    </div>
  );
}

export function AssociatedDoctors(prps: AssociatedDoctorProps) {
  const [doctorList, setDoctorList] = useState<Array<PaginatedDoctorItem>>([]);
  const [doctorLoading, setDoctorLoading] = useState<boolean>(false);
  const [itemsOnTheStack, setItemsOnTheStack] = useState<
    Array<{
      side: 'left' | 'right';
      id: number;
    }>
  >([]);
  const [associated, setAssociated] = useState<Array<GetAssociatedDoctorItem>>(
    [],
  );
  const [available, setAvailable] = useState<Array<GetAssociatedDoctorItem>>(
    [],
  );
  const [triggerRender, setTrigger] = useState<number>(0);
  const [associationLoader, setAssociationLoader] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');

  useEffect(() => {
    setDoctorLoading(true);
    getPaginatedDoctors(prps.accessToken, 0, 10, search).then((resp) => {
      if (typeof resp === 'number') {
        showToast({
          message: 'Unable to fetch doctors',
          type: 'info'
        })
        setDoctorLoading(false);
        return;
      }
      setDoctorList(resp.data as Array<PaginatedDoctorItem>);
      setDoctorLoading(false);
    });
  }, [prps.accessToken, search, triggerRender]);

  useEffect(() => {
    if (!prps.clinciId || !prps.accessToken) {
      console.error('Clinic Id or Access Token was not Found');
      return;
    }
    setAssociationLoader(true);

    getAssociatedClinics(prps.accessToken, prps.clinciId).then((res) => {
      if (typeof res === 'number') {
        showToast({
          message: 'Unable to fetch associated clinics',
          type: 'info'
        })
        setAssociationLoader(false);
        return;
      }

      setAssociated(res);
      setAssociationLoader(false);
    });
  }, [prps.accessToken, triggerRender, prps.clinciId]);

  const moveSelectedToTarget = async (side: 'left' | 'right') => {
    if (side === 'right') {
      const itemsToMove = itemsOnTheStack
        .filter((iS) => iS.side === 'left')
        .map((el) => el.id);

      const status = await addAssociatedClinics(
        prps.accessToken,
        prps.clinciId,
        itemsToMove,
      );
      if (status === 201) {
        showToast({
          message: 'Doctor List Updated',
          type: 'success'
        })
        // Removing Items from Stack
        setItemsOnTheStack((prev) =>
          prev.filter((p) => !itemsToMove.includes(p.id)),
        );
        setTrigger((p) => p + 1);
        return;
      }
      showToast({
        message: 'Unable to update doctor list!',
        type: 'info'
      })
      return;
    } else {
      const itemsToMove = itemsOnTheStack
        .filter((iS) => iS.side === 'right')
        .map((e) => e.id);

      const status = await breakAssociations(
        prps.accessToken,
        prps.clinciId,
        itemsToMove,
      );

      if (status === 200) {
        showToast({
          message: 'Doctor List Updated!',
          type: 'success'
        })
        // Removing Items from Stack
        setItemsOnTheStack((prev) =>
          prev.filter((p) => !itemsToMove.includes(p.id)),
        );
        setTrigger((p) => p + 1);
        return;
      }
      showToast({
        message: 'Unable to update doctor list!',
        type: 'info'
      })
      return;
    }
  };

  useEffect(() => {
    const associatedIdentifiers = associated?.map((a) => a.id);

    setAvailable(() => {
      return doctorList
        ?.filter((dl) => !associatedIdentifiers?.includes(dl.id))
        ?.map((filtered) => ({
          id: filtered.id,
          firstName: filtered.firstName,
          lastName: filtered.lastName,
          hpi: filtered.hpi,
          aphraNumber: filtered.ahpraNumber,
          email: filtered.email,
        }));
    });
  }, [associated, doctorList]);

  return (
    <div className="space-y-10 divide-y divide-gray-900/10 add-patient">
      <div className="p-7 bg-secondary-pink-50 shadow rounded-xl mb-10">
        <h3 className="mb-3 text-lg font-semibold text-primary-blue-500">
          Associations
        </h3>
        <div className="px-6 py-7 bg-white rounded-md">
          <PickList
            moveSelected={(side) => moveSelectedToTarget(side)}
            left={{
              title: 'Available',
              loading: doctorLoading,
              isActionEnabled: itemsOnTheStack.some((iS) => iS.side == 'left'),
              setSearchInputInfo: (ip) => setSearch(ip),
              items: available.map((dl, i) => (
                <PickListCard
                  key={i}
                  name={`${dl.firstName} ${dl.lastName}`}
                  email={dl.email}
                  itemsOnTheStack={itemsOnTheStack}
                  id={dl.id}
                  side="left"
                  onStack={() =>
                    setItemsOnTheStack((p) => [
                      ...p,
                      { side: 'left', id: dl.id },
                    ])
                  }
                  onRemove={() =>
                    setItemsOnTheStack((p) =>
                      p.filter((pre) => pre.id !== dl.id),
                    )
                  }
                />
              )),
            }}
            right={{
              title: 'Associated',
              loading: associationLoader,
              isActionEnabled: itemsOnTheStack.some((iS) => iS.side == 'right'),
              items: associated.map((a, i) => (
                <PickListCard
                  key={i}
                  name={`${a.firstName} ${a.lastName}`}
                  email={a.email}
                  itemsOnTheStack={itemsOnTheStack}
                  id={a.id}
                  side="right"
                  onStack={() =>
                    setItemsOnTheStack((p) => [
                      ...p,
                      { side: 'right', id: a.id },
                    ])
                  }
                  onRemove={() =>
                    setItemsOnTheStack((p) =>
                      p.filter((pre) => pre.id !== a.id),
                    )
                  }
                />
              )),
            }}
          />
        </div>
      </div>
    </div>
  );
}
